import React, { useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, ExternalLink, Users, Clock, Coins, AlertTriangle } from 'lucide-react';
interface Guard {
  id: string;
  name: string;
  grade: 'A' | 'B' | 'C';
  shifts: ShiftDay[];
}
interface ShiftDay {
  // Position within the cycle (1..cycleDays.length)
  day: number;
  type: 'day' | 'night' | 'overtime' | 'sunday-shift' | 'sunday-off' | null;
}
interface CycleDay {
  // 1-based position within the cycle
  index: number;
  // Calendar day label to display (e.g., 16, 17 ... 31, 1, 2 ... 15)
  label: number;
  // Day of week: 0 = Sunday, 6 = Saturday
  dow: number;
}
interface RosterModalProps {
  isOpen: boolean;
  siteName: string;
  siteId: string;
  onClose: () => void;
  onTakeAction: () => void;
}
// Build the 16th-of-prior-month → 15th-of-current-month cycle for a given month
const buildCycleDays = (year: number, month: number): CycleDay[] => {
  const start = new Date(year, month - 1, 16); // 16th of prior month
  const end = new Date(year, month, 15); // 15th of current month
  const days: CycleDay[] = [];
  let cursor = new Date(start);
  let index = 1;
  while (cursor.getTime() <= end.getTime()) {
    days.push({
      index,
      label: cursor.getDate(),
      dow: cursor.getDay()
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    index++;
  }
  return days;
};
// Mock data generator for guards and their shifts
const generateMockGuards = (siteId: string, cycleDays: CycleDay[]): Guard[] => {
  const guardNames = [{
    name: 'John Mkhize',
    grade: 'A' as const
  }, {
    name: 'Thabo Ndlovu',
    grade: 'B' as const
  }, {
    name: 'Sipho Dlamini',
    grade: 'A' as const
  }, {
    name: 'Nomsa Khumalo',
    grade: 'B' as const
  }, {
    name: 'Bongani Zulu',
    grade: 'C' as const
  }, {
    name: 'Lindiwe Moyo',
    grade: 'B' as const
  }, {
    name: 'Mandla Nkosi',
    grade: 'A' as const
  }, {
    name: 'Zanele Sithole',
    grade: 'C' as const
  }];
  const cycleLength = cycleDays.length;
  const seed = siteId.length;
  const guardCount = 6 + seed % 2;
  // Per-guard targets: top 5 are overtime guards with higher totals
  const targetTotals = [25, 23, 22, 22, 20, 17, 16, 18];
  const overtimeTargets = [5, 4, 3, 3, 1, 0, 0, 0];
  // dow lookup by cycle index (1-based)
  const dowOf = (cycleIndex: number) => cycleDays[cycleIndex - 1].dow;
  const guards: Guard[] = guardNames.slice(0, guardCount).map((guard, guardIndex) => {
    const target = Math.min(targetTotals[guardIndex] ?? 17, cycleLength);
    const overtimeTarget = overtimeTargets[guardIndex] ?? 0;
    // Some guards work Sundays (mix of overtime and non-overtime guards)
    const sundayWorkerProfile: Record<number, 'most' | 'some' | 'none'> = {
      0: 'most',
      2: 'most',
      3: 'some',
      5: 'most',
      6: 'some'
    };
    const sundayProfile = sundayWorkerProfile[guardIndex] ?? 'none';
    // Score each cycle day for scheduling preference
    const dayScores = cycleDays.map(cd => {
      const {
        index: day,
        dow
      } = cd;
      let score = 0;
      if (dow === 0) {
        if (sundayProfile === 'most') score = 95;else if (sundayProfile === 'some') score = 60;else score = 10;
      } else if (dow === 6) {
        score = 50;
      } else {
        score = 100;
      }
      // Deterministic noise so guards don't all pick identical days
      const noise = (day * 31 + guardIndex * 17) % 13 - 6;
      score += noise;
      // Ensure cycle day 7 is rostered by guards 1 and 4 (prevents empty column)
      if (day === 7 && (guardIndex === 1 || guardIndex === 4)) {
        score = 110;
      }
      return {
        day,
        dow,
        score
      };
    });
    // Sort by score desc, take top `target` as worked days
    const sortedByScore = [...dayScores].sort((a, b) => b.score - a.score);
    const workedDays = new Set(sortedByScore.slice(0, target).map(d => d.day));
    // Build shifts in cycle order
    const shifts: ShiftDay[] = [];
    for (let day = 1; day <= cycleLength; day++) {
      const dow = dowOf(day);
      if (workedDays.has(day)) {
        if (dow === 0) {
          shifts.push({
            day,
            type: 'sunday-shift'
          });
        } else {
          const type: 'day' | 'night' = (day + guardIndex) % 3 === 0 ? 'night' : 'day';
          shifts.push({
            day,
            type
          });
        }
      } else if (dow === 0) {
        shifts.push({
          day,
          type: 'sunday-off'
        });
      } else {
        shifts.push({
          day,
          type: null
        });
      }
    }
    return {
      id: `guard-${guardIndex}`,
      name: guard.name,
      grade: guard.grade,
      shifts
    };
  });
  // --- Manual overrides ---
  // Add 1 day shift + 1 night shift on the columns for calendar labels 17, 31 and 7.
  //    Find each target cycle index by matching the calendar label.
  //    Label 17 → Jan 17, Label 31 → Jan 31, Label 7 → Feb 7.
  //    These are weekdays (no Sunday handling needed).
  const targetLabels = [17, 31, 7];
  targetLabels.forEach(label => {
    // Match the first occurrence of this label in the cycle
    const target = cycleDays.find(cd => cd.label === label);
    if (!target) return;
    const colIndex = target.index - 1;
    // Find guards currently with no shift in this column
    const idleGuards = guards.map((g, gi) => ({
      g,
      gi
    })).filter(({
      g
    }) => g.shifts[colIndex].type === null);
    if (idleGuards.length >= 1) {
      idleGuards[0].g.shifts[colIndex] = {
        day: target.index,
        type: 'day'
      };
    }
    if (idleGuards.length >= 2) {
      idleGuards[1].g.shifts[colIndex] = {
        day: target.index,
        type: 'night'
      };
    }
  });
  // --- Overtime rule ---
  // Walk each guard's shifts chronologically. Count non-Sunday worked shifts
  // (day/night). The 20th and onwards become overtime. Sunday shifts never
  // count toward the 19-shift threshold and are never converted.
  guards.forEach(guard => {
    let normalCount = 0;
    for (const s of guard.shifts) {
      if (s.type === 'day' || s.type === 'night') {
        normalCount += 1;
        if (normalCount > 19) {
          s.type = 'overtime';
        }
      }
    }
  });
  // --- Per-row visual overrides (for demo realism) ---
  // Row 1 (guard 0 — John Mkhize): make the last 7 non-Sunday worked shifts
  // overtime (red). The base rule above only converts after the 19th; we
  // additionally convert positions 18 and 19 so the row ends with 7 red cells.
  if (guards[0]) {
    const workedIdx = guards[0].shifts.map((s, i) => ({
      s,
      i
    })).filter(({
      s
    }) => s.type === 'day' || s.type === 'night' || s.type === 'overtime');
    workedIdx.forEach((entry, pos) => {
      // pos is 0-based chronological index of non-Sunday worked shifts
      if (pos >= 17) {
        guards[0].shifts[entry.i].type = 'overtime';
      }
    });
  }
  // Row 2 (guard 1 — Thabo Ndlovu): remove shifts at Jan 24 (cycle index 9)
  // and Feb 2 (cycle index 18) — make them not rostered.
  if (guards[1]) {
    ;
    [9, 18].forEach(cycleIndex => {
      const slot = guards[1].shifts[cycleIndex - 1];
      if (slot) slot.type = null;
    });
  }
  // Row 3 (guard 2 — Sipho Dlamini): clear Sunday shift on Feb 15 (index 31)
  // and force overtime on Feb 12 (index 28) and Feb 13 (index 29).
  if (guards[2]) {
    const sundaySlot = guards[2].shifts[30]; // cycle index 31 (0-based 30)
    if (sundaySlot) sundaySlot.type = 'sunday-off';
    [28, 29].forEach(cycleIndex => {
      const slot = guards[2].shifts[cycleIndex - 1];
      if (slot) slot.type = 'overtime';
    });
  }
  return guards;
};
const getMonthName = (month: number): string => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month];
};
export function RosterModal({
  isOpen,
  siteName,
  siteId,
  onClose,
  onTakeAction
}: RosterModalProps) {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1)); // February 2026
  const [guards, setGuards] = useState<Guard[]>([]);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const cycleDays = useMemo(() => buildCycleDays(year, month), [year, month]);
  const cycleLength = cycleDays.length;
  // Generate guards when modal opens or cycle changes
  useEffect(() => {
    if (isOpen) {
      setGuards(generateMockGuards(siteId, cycleDays));
    }
  }, [isOpen, siteId, cycleDays]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  // Calculate summary stats — sunday shifts count toward total but never as overtime
  const isWorkedShift = (t: ShiftDay['type']) => t === 'day' || t === 'night' || t === 'overtime' || t === 'sunday-shift';
  const totalShiftsScheduled = guards.reduce((sum, guard) => sum + guard.shifts.filter(s => isWorkedShift(s.type)).length, 0);
  const requiredShifts = guards.length * 19;
  const overtimeShifts = guards.reduce((sum, guard) => sum + guard.shifts.filter(s => s.type === 'overtime').length, 0);
  const understaffedBy = Math.max(0, requiredShifts - totalShiftsScheduled);
  // Widget stats — derived from the guards rows shown in the modal
  const HOURS_PER_SHIFT = 12;
  const OT_HOURLY_RATE = 15.66; // R per overtime hour (avoidable premium)
  const guardsCount = guards.length;
  const overtimeHours = overtimeShifts * HOURS_PER_SHIFT;
  const avoidableOTCost = Math.round(overtimeHours * OT_HOURLY_RATE);
  const guardsOverThreshold = guards.filter(g => g.shifts.filter(s => isWorkedShift(s.type)).length > 19).length;
  const formatCurrency = (n: number) => `R ${n.toLocaleString('en-ZA').replace(/,/g, ' ')}`;
  const getShiftCountColor = (count: number): string => {
    if (count > 19) return 'text-rose-600 font-semibold';
    if (count === 19) return 'text-amber-600 font-semibold';
    return 'text-[#5a7280]';
  };
  const getGradeBadgeColor = (grade: 'A' | 'B' | 'C'): string => {
    switch (grade) {
      case 'A':
        return 'bg-emerald-100 text-emerald-700';
      case 'B':
        return 'bg-sky-100 text-sky-700';
      case 'C':
        return 'bg-amber-100 text-amber-700';
    }
  };
  const getShiftCellStyle = (type: ShiftDay['type']): string => {
    switch (type) {
      case 'day':
        return 'bg-sky-500';
      case 'night':
        return 'bg-[#062133]';
      case 'overtime':
        return 'bg-rose-500';
      case 'sunday-shift':
        return 'bg-violet-500';
      case 'sunday-off':
        return 'bg-[#f0ebe4]';
      default:
        return 'bg-[#e5e5e5]';
    }
  };
  // Cycle label e.g. "16 Jan – 15 Feb 2026"
  const cycleLabel = (() => {
    const start = new Date(year, month - 1, 16);
    const end = new Date(year, month, 15);
    const startMonth = getMonthName(start.getMonth()).slice(0, 3);
    const endMonth = getMonthName(end.getMonth()).slice(0, 3);
    return `16 ${startMonth} – 15 ${endMonth} ${end.getFullYear()}`;
  })();
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#062133]/40 backdrop-blur-sm z-40 transition-opacity duration-300" onClick={onClose} aria-hidden="true" data-id="element-959" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="roster-modal-title" data-id="element-960">
        <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1400px] max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-961">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5] flex-shrink-0" data-id="element-962">
            <div className="flex items-center gap-3" data-id="element-963">
              <Calendar className="h-6 w-6 text-[#ff697f]" data-id="element-964" />
              <h2 id="roster-modal-title" className="text-xl font-bold text-[#062133]" data-id="element-965">
                {siteName} - Monthly Roster
              </h2>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors" aria-label="Close modal" data-id="element-966">
              <X className="h-5 w-5" data-id="element-967" />
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-center gap-4 px-6 py-4 border-b border-[#e5e5e5] bg-[#fafaf9] flex-shrink-0" data-id="element-968">
            <button onClick={handlePrevMonth} className="p-2 text-[#5a7280] hover:text-[#062133] hover:bg-white rounded-lg transition-colors" aria-label="Previous cycle" data-id="element-969">
              <ChevronLeft className="h-5 w-5" data-id="element-970" />
            </button>
            <div className="flex flex-col items-center min-w-[220px]" data-id="element-971">
              <span className="text-lg font-semibold text-[#062133]" data-id="element-972">
                {getMonthName(month)} {year}
              </span>
              <span className="text-xs text-[#5a7280]" data-id="element-973">{cycleLabel}</span>
            </div>
            <button onClick={handleNextMonth} className="p-2 text-[#5a7280] hover:text-[#062133] hover:bg-white rounded-lg transition-colors" aria-label="Next cycle" data-id="element-974">
              <ChevronRight className="h-5 w-5" data-id="element-975" />
            </button>
          </div>

          {/* Stat Widgets */}
          <div className="px-6 pt-6 flex-shrink-0" data-id="element-976">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-977">
              <div className="bg-white rounded-xl p-4 border border-[#e5e5e5]" data-id="element-978">
                <div className="flex items-center gap-2 mb-2" data-id="element-979">
                  <Users className="w-4 h-4 text-[#5a7280]" data-id="element-980" />
                  <span className="text-[11px] font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-981">
                    Guards
                  </span>
                </div>
                <span className="text-2xl font-bold text-[#062133]" data-id="element-982">
                  {guardsCount}
                </span>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#e5e5e5]" data-id="element-983">
                <div className="flex items-center gap-2 mb-2" data-id="element-984">
                  <Clock className="w-4 h-4 text-[#5a7280]" data-id="element-985" />
                  <span className="text-[11px] font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-986">
                    Overtime Hours
                  </span>
                </div>
                <span className={`text-2xl font-bold ${overtimeHours > 0 ? 'text-[#ff697f]' : 'text-[#062133]'}`} data-id="element-987">
                  {overtimeHours}h
                </span>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#e5e5e5]" data-id="element-988">
                <div className="flex items-center gap-2 mb-2" data-id="element-989">
                  <Coins className="w-4 h-4 text-[#5a7280]" data-id="element-990" />
                  <span className="text-[11px] font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-991">
                    Avoidable OT Cost
                  </span>
                </div>
                <span className="text-2xl font-bold text-[#062133]" data-id="element-992">
                  {formatCurrency(avoidableOTCost)}
                </span>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#e5e5e5]" data-id="element-993">
                <div className="flex items-center gap-2 mb-2" data-id="element-994">
                  <AlertTriangle className="w-4 h-4 text-[#5a7280]" data-id="element-995" />
                  <span className="text-[11px] font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-996">
                    Over Threshold
                  </span>
                </div>
                <span className={`text-2xl font-bold ${guardsOverThreshold > 0 ? 'text-[#ff697f]' : 'text-[#062133]'}`} data-id="element-997">
                  <span className={guardsOverThreshold > 0 ? '' : 'text-[#062133]'} data-id="element-998">
                    {guardsOverThreshold}
                  </span>
                  <span className="text-[#062133] font-bold" data-id="element-999">
                    {' '}
                    of {guardsCount}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Roster Grid */}
          <div className="flex-1 overflow-auto p-6" data-id="element-1000">
            <div data-id="element-1001">
              {/* Header Row - Days */}
              <div className="flex items-center mb-2" data-id="element-1002">
                <div className="w-[180px] flex-shrink-0" data-id="element-1003" />
                <div className="flex-1 grid gap-0.5" style={{
                gridTemplateColumns: `repeat(${cycleLength}, minmax(0, 1fr))`
              }} data-id="element-1004">
                  {cycleDays.map(cd => {
                  const isSunday = cd.dow === 0;
                  return <div key={cd.index} className={`aspect-square max-h-8 flex items-center justify-center text-[10px] font-medium ${isSunday ? 'text-[#a89b87]' : 'text-[#5a7280]'}`} data-id="element-1005">
                        {cd.label}
                      </div>;
                })}
                </div>
              </div>

              {/* Guard Rows */}
              {guards.map(guard => {
              const shiftCount = guard.shifts.filter(s => isWorkedShift(s.type)).length;
              return <div key={guard.id} className="flex items-center mb-1.5 group" data-id="element-1006">
                    {/* Guard Info */}
                    <div className="w-[180px] flex-shrink-0 pr-3 flex items-center gap-2" data-id="element-1007">
                      <div className="flex-1 min-w-0" data-id="element-1008">
                        <p className="text-sm font-medium text-[#062133] truncate" data-id="element-1009">
                          {guard.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-1010">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getGradeBadgeColor(guard.grade)}`} data-id="element-1011">
                            Grade {guard.grade}
                          </span>
                          <span className={`text-xs ${getShiftCountColor(shiftCount)}`} data-id="element-1012">
                            {shiftCount} shifts
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Shift Cells */}
                    <div className="flex-1 grid gap-0.5" style={{
                  gridTemplateColumns: `repeat(${cycleLength}, minmax(0, 1fr))`
                }} data-id="element-1013">
                      {guard.shifts.map((shift, index) => {
                    const titleMap: Record<string, string> = {
                      day: 'Day shift',
                      night: 'Night shift',
                      overtime: 'Overtime shift',
                      'sunday-shift': 'Sunday shift (excl. from overtime)',
                      'sunday-off': 'Sunday — not rostered'
                    };
                    return <div key={index} className={`aspect-square max-h-8 rounded ${getShiftCellStyle(shift.type)} transition-transform hover:scale-110`} title={shift.type ? titleMap[shift.type] : 'No shift'} data-id="element-1014" />;
                  })}
                    </div>
                  </div>;
            })}

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 pt-4 border-t border-[#e5e5e5]" data-id="element-1015">
                <span className="text-xs font-medium text-[#5a7280] uppercase tracking-wider" data-id="element-1016">
                  Legend:
                </span>
                <div className="flex items-center gap-2" data-id="element-1017">
                  <div className="w-4 h-4 rounded bg-sky-500" data-id="element-1018" />
                  <span className="text-xs text-[#5a7280]" data-id="element-1019">Day Shift</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1020">
                  <div className="w-4 h-4 rounded bg-[#062133]" data-id="element-1021" />
                  <span className="text-xs text-[#5a7280]" data-id="element-1022">Night Shift</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1023">
                  <div className="w-4 h-4 rounded bg-rose-500" data-id="element-1024" />
                  <span className="text-xs text-[#5a7280]" data-id="element-1025">Overtime</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1026">
                  <div className="w-4 h-4 rounded bg-violet-500" data-id="element-1027" />
                  <span className="text-xs text-[#5a7280]" data-id="element-1028">
                    Sunday Shift (excl. from overtime)
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1029">
                  <div className="w-4 h-4 rounded bg-[#e5e5e5]" data-id="element-1030" />
                  <span className="text-xs text-[#5a7280]" data-id="element-1031">Not Rostered</span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Bar */}
          <div className="px-6 py-4 border-t border-[#e5e5e5] bg-[#fafaf9] flex-shrink-0" data-id="element-1032">
            <div className="flex flex-wrap items-center gap-6" data-id="element-1033">
              <div className="flex items-center gap-2" data-id="element-1034">
                <span className="text-sm text-[#5a7280]" data-id="element-1035">Total Scheduled:</span>
                <span className="text-sm font-semibold text-[#062133]" data-id="element-1036">
                  {totalShiftsScheduled}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1037">
                <span className="text-sm text-[#5a7280]" data-id="element-1038">Required:</span>
                <span className="text-sm font-semibold text-[#062133]" data-id="element-1039">
                  {requiredShifts}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1040">
                <span className="text-sm text-[#5a7280]" data-id="element-1041">Overtime Shifts:</span>
                <span className={`text-sm font-semibold ${overtimeShifts > 0 ? 'text-rose-600' : 'text-[#062133]'}`} data-id="element-1042">
                  {overtimeShifts}
                </span>
              </div>
              {understaffedBy > 0 && <div className="flex items-center gap-2" data-id="element-1043">
                  <span className="text-sm text-[#5a7280]" data-id="element-1044">
                    Understaffed by:
                  </span>
                  <span className="text-sm font-semibold text-amber-600" data-id="element-1045">
                    {understaffedBy} shifts
                  </span>
                </div>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-5 border-t border-[#e5e5e5] flex-shrink-0" data-id="element-1046">
            <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-[#5a7280] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:text-[#062133] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] transition-colors" data-id="element-1047">
              Close
            </button>
            <button onClick={() => {
            onClose();
            onTakeAction();
          }} className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-[#ff697f] rounded-xl hover:bg-[#e65a6f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff697f] transition-colors shadow-sm" data-id="element-1048">
              Take Action
              <ExternalLink className="ml-2 h-4 w-4" data-id="element-1049" />
            </button>
          </div>
        </div>
      </div>
    </>;
}